import { Box, ClickAwayListener, Fade } from '@mui/material';
import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  BehaviorSubject,
  debounce,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  merge,
  Observable,
  of,
  switchMap,
  timer,
} from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { SearchResultObject, SearchState, SearchStatus } from './types';
import type { SearchResultComponent } from './SearchResult';
import {
  FOCUSABLE_ELEMENT_CLASS,
  SEARCH_TERM_PARAM_KEY,
  TAB_LIST_WIDTH,
} from './Constants';
import { AmplitudeSearchEvents, useAmplitude } from '../nav/hooks/useAmplitude';
import { useSearchableMenuItems } from './useSearchableMenuItems';
import { deepActiveElement, isAppleDevice } from './utils';
import { SearchInput } from './SearchInput';
import { SearchResult } from './SearchResult';
import { getEndpoint } from '../nav/utils';
import { NAVIGATION_ENDPOINTS } from '../nav/constants/urls';

const DEBOUNCE_TIME = 500;
const MIN_CHAR_LENGTH = 3;
function searchCount(keyword: string): Observable<Partial<SearchState>> {
  const path = `${NAVIGATION_ENDPOINTS.SEARCH_COUNT}?q=${encodeURIComponent(
    keyword,
  )}`;
  return fromFetch(getEndpoint(window.location, path), {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  }).pipe(
    mergeMap((res) => {
      return !res?.ok
        ? of({ error: { message: res.statusText ?? 'Network Error' } })
        : res.json();
    }),
    map((response) => {
      return response.success
        ? {
            status: SearchStatus.Success,
            data: response.data,
          }
        : {
            status: SearchStatus.Error,
            error: response?.error,
          };
    }),
  );
}

function quickSearch(keyword: string): Observable<Partial<SearchState>> {
  const path = `${
    NAVIGATION_ENDPOINTS.SEARCH_QUICK_SEARCH
  }?q=${encodeURIComponent(keyword)}`;
  return fromFetch(getEndpoint(window.location, path), {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  }).pipe(
    mergeMap((res) => {
      return !res?.ok
        ? of({ error: { message: res.statusText ?? 'Network Error' } })
        : res.json();
    }),
    map((response) => {
      return response.success
        ? {
            status: SearchStatus.Success,
            data: response.data,
          }
        : {
            status: SearchStatus.Error,
            error: response?.error,
          };
    }),
  );
}
function performSearchCount(keyword: string): Observable<Partial<SearchState>> {
  if (keyword.length === 0) {
    return of({ status: SearchStatus.Idle, data: [] });
  }

  const searchCountObservable = merge(
    of({ status: SearchStatus.Loading, data: [] }),
    searchCount(keyword).pipe(
      map((state) => {
        if (state.status === SearchStatus.Success && state.data) {
          return {
            ...state,
            data: [...state.data],
          };
        }
        if (state.status === SearchStatus.Error) {
          return {
            status: SearchStatus.Success,
            data: [],
          };
        }
        return state;
      }),
    ),
  );

  return searchCountObservable;
}

function performSearch(
  keyword: string,
  status: SearchStatus,
  searchNavItems: (keyword: string) => SearchResultObject,
  logEvent,
): Observable<Partial<SearchState>> {
  if (keyword.length === 0) {
    return of({ status: SearchStatus.Idle, data: [] });
  }
  logEvent(AmplitudeSearchEvents.QUICK_SEARCH, { searchTerm: keyword });

  let navResult;
  try {
    navResult = searchNavItems(keyword);
  } catch (e) {
    console.error(e);
  }

  const hasNavResult = !!navResult?.objects?.length;

  const quickSearchObservable = merge(
    of({ status: SearchStatus.Loading, data: [] }),
    quickSearch(keyword).pipe(
      map((state) => {
        if (
          state.status === SearchStatus.Success &&
          state.data &&
          hasNavResult
        ) {
          return {
            ...state,
            data: [...state.data, navResult],
          };
        }
        if (state.status === SearchStatus.Error && hasNavResult) {
          return {
            status: SearchStatus.Success,
            data: [navResult],
          };
        }
        return state;
      }),
    ),
  );

  return quickSearchObservable;
}

export const SearchContext = createContext<{
  keyword: string;
  setKeyword: (keyword: string) => void;
}>({
  keyword: '',
  setKeyword: () => {},
});

export const Search: React.FC = () => {
  const param = new URLSearchParams(window.location.search);
  const defaultSearchTerm = param.get(SEARCH_TERM_PARAM_KEY) || '';

  const [showSearchResult, setShowSearchResult] = useState(false);
  const [subject] = useState<BehaviorSubject<string>>(
    new BehaviorSubject(defaultSearchTerm),
  );
  const [keyword, setKeyword] = useState<string>(defaultSearchTerm);
  const [quickSearchState, setQuickSearchState] = useState<SearchState>({
    status: SearchStatus.Idle,
    data: [],
  });
  const [searchCountState, setSearchCountState] = useState<SearchState>({
    status: SearchStatus.Idle,
    data: [],
  });
  const [debounceEnabled, setDebounceEnabled] = useState(true);
  const { logEvent } = useAmplitude();
  const inputRef = useRef<HTMLInputElement>();
  const childRef = useRef<SearchResultComponent>(null);
  const { menus, searchMenu } = useSearchableMenuItems();
  const showSearchBox = (usingShortcut: boolean) => {
    if (!showSearchResult) {
      logEvent(AmplitudeSearchEvents.OPEN_SEARCH_BAR, {
        usingShortcut: usingShortcut ? 'true' : 'false',
      });
      setShowSearchResult(true);
    }
  };

  const hideSearchBox = (usingShortcut: boolean) => {
    if (showSearchResult) {
      setShowSearchResult(false);
      logEvent(AmplitudeSearchEvents.CLOSE_SEARCH_BAR, {
        usingShortcut: usingShortcut ? 'true' : 'false',
      });
    }
  };

  const handleInputChange = useCallback(
    (e) => {
      setDebounceEnabled(true);
      setKeyword(e.target.value);
      subject.next(e.target.value);
    },
    [subject, setKeyword, setDebounceEnabled],
  );

  const setKeywordByChildren = useCallback(
    (keyword) => {
      setDebounceEnabled(false);
      setKeyword(keyword);
      subject.next(keyword);
      inputRef.current?.focus();
    },
    [subject, setKeyword, setDebounceEnabled],
  );

  const goToAdvancedSearch = useCallback(
    (value: string) => {
      console.log('goToAdvancedSearch', value);
      if (!value || value.length < MIN_CHAR_LENGTH) return;
      childRef.current?.goToAdvancedPage();
    },
    [childRef],
  );

  useEffect(() => {
    const searchTermObservable = subject.pipe(
      map((keyword) => keyword.trim()),
      filter(
        (keyword) => keyword.length === 0 || keyword.length >= MIN_CHAR_LENGTH,
      ),
      debounce((keyword) =>
        keyword.length > 0 && debounceEnabled ? timer(DEBOUNCE_TIME) : timer(0),
      ),
      distinctUntilChanged(),
    );

    const searchCountSubscription = searchTermObservable
      .pipe(
        switchMap((keyword) => {
          return performSearchCount(keyword);
        }),
      )
      .subscribe((searchCountValue) => {
        setSearchCountState((prev) =>
          Object.assign({}, prev, searchCountValue),
        );
      });

    const quickSearchSubscription = searchTermObservable
      .pipe(
        switchMap((keyword) => {
          return performSearch(
            keyword,
            quickSearchState.status,
            searchMenu,
            logEvent,
          );
        }),
      )
      .subscribe((quickSearchValue) => {
        setQuickSearchState((prev) =>
          Object.assign({}, prev, quickSearchValue),
        );
      });

    return () => {
      searchCountSubscription.unsubscribe();
      quickSearchSubscription.unsubscribe();
    };
  }, [debounceEnabled, menus]);

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'k' && (e.ctrlKey || e.metaKey)) {
        showSearchBox(true);
        inputRef.current?.focus();
        e.preventDefault();
      } else if (e.key === 'Escape') {
        inputRef.current?.blur();
        hideSearchBox(true);
      }
    }
    document.addEventListener('keydown', handleKeyDown, false);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showSearchResult]);

  useEffect(() => {
    function handleBlur(e) {
      if (e.key === 'Tab') {
        if (!deepActiveElement().className.includes(FOCUSABLE_ELEMENT_CLASS)) {
          hideSearchBox(false);
        }
      }
    }
    document.addEventListener('keyup', handleBlur, false);
    return () => document.removeEventListener('keyup', handleBlur);
  }, [showSearchResult]);

  const shortcutHintText = isAppleDevice() ? '⌘K' : 'Ctrl+K';
  const hintText = showSearchResult ? 'Esc' : shortcutHintText;

  return (
    <SearchContext.Provider
      value={{ keyword, setKeyword: setKeywordByChildren }}
    >
      <ClickAwayListener onClickAway={() => hideSearchBox(false)}>
        <Box
          sx={{
            width: '100%',
            height: '100%',
          }}
        >
          <SearchInput
            value={keyword}
            onClickOrFocus={() => showSearchBox(false)}
            onChange={handleInputChange}
            ref={inputRef}
            hintText={hintText}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                goToAdvancedSearch(keyword);
              } else if (e.key === 'ArrowDown') {
                if (showSearchResult) {
                  childRef.current?.focusFirstResult();
                  e.preventDefault();
                }
              }
            }}
          />
          {showSearchResult && (
            <Fade in={showSearchResult}>
              <Box
                data-e2e="search-result-box"
                sx={{
                  zIndex: '1000',
                  boxSizing: 'border-box',
                  width: `calc(100% + ${TAB_LIST_WIDTH}px)`,
                  position: 'relative',
                  left: `-${TAB_LIST_WIDTH}px`,
                  borderRadius: '0 0 4px 4px',
                }}
              >
                <SearchResult
                  state={[quickSearchState, searchCountState]}
                  ref={childRef}
                />
              </Box>
            </Fade>
          )}
        </Box>
      </ClickAwayListener>
    </SearchContext.Provider>
  );
};
